import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ProductSetType } from 'src/types/productSet';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import {
    clearProductSetDetail,
    createProductSetDetailFetch,
    deleteProductSetFetch,
    getProductSetDetailFetch,
    updateProductSetDetailFetch
} from 'src/resources/productSetDetail/reducer';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import ProductsTable from './ProductsTable';
import TopBlock from './TopBlock';
import getBackUrl from 'src/utils/getBackUrl';
import { EDIT_KEYS, EditKeysType, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import NewProductBlock from './NewProductBlock';
import { sizes } from 'src/themes/default';
import { setStorageData } from 'src/resources/storage/reducer';
import isDeepEqual from 'src/utils/isDeepEqual';
import { samePaths } from 'src/utils/checkPaths';
import getArrToObjectOfTrues from 'src/utils/getArrToObjectOfTrues';
import { FormErrorsType, OnChangeType } from '../../types/global';

const initData = {};
const ProductSetDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { skuProductSet } = useParams();
    const setSKU = skuProductSet && decodeURIComponent(skuProductSet);

    const newProductSet = !setSKU;

    const formRef = useRef<HTMLFormElement>(null);
    const productsFormRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.productSetDetail.data);
    const loading = useSelector((state: RootStoreType) => state.productSetDetail.loading);
    const apiErrorKeys = useSelector((state: RootStoreType) => state.productSetDetail.apiErrorKeys);
    const deleted = useSelector((state: RootStoreType) => state.productSetDetail.deleted);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const [editData, setEditData] = useState<ProductSetType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const disableEdit = true; // Always read-only now
    const permissionDelete = false; // Disable delete

    const backUrl = getBackUrl(locationHistory, '/products/sets/', '/products/sets/new/');

    const handleNavigateToProductDetail = () => {
        if (setSKU) {
            navigate(`/products?search=${encodeURIComponent(setSKU)}`);
        }
    };

    useEffect(() => {
        if (setSKU) {
            dispatch(setLocationParams({ name: translate('productSetDetail.title', { setSKU }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('productSetDetail.titleNew'), backUrl }));
        }
    }, [setSKU, backUrl, locationHistory]);

    useEffect(() => {
        if (setSKU) {
            dispatch(getProductSetDetailFetch(setSKU));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearProductSetDetail());
        };
    }, [setSKU]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!setSKU && data?.SET_SKU) {
            navigate('/products/sets/' + encodeURIComponent(String(data.SET_SKU)));
            setThisStorageData(null);
        }
    }, [data]);

    useEffect(() => {
        setEditErrors(getArrToObjectOfTrues(apiErrorKeys));
    }, [apiErrorKeys]);

    useEffect(() => {
        if (deleted) {
            navigate(backUrl);
        }
    }, [deleted]);

    // save edit data -start
    const editDataRef = useRef<{ data: ProductSetType; new: boolean }>({ data: {}, new: false });
    useEffect(() => {
        if (
            newProductSet &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/products/sets') ||
                samePaths(locationHistory[1]?.pathname, '/products/sets/new'))
        ) {
            setConfirmParams({
                title: translate('productSetDetail.confirmGetNewDataStorage'),
                content: translate('productSetDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                }
            });
        }
        return () => {
            if (editDataRef.current?.new) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current.data);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = { ...editDataRef.current, data: editData };
    }, [editData]);

    useEffect(() => {
        editDataRef.current = { ...editDataRef.current, new: newProductSet };
    }, [newProductSet]);

    const setThisStorageData = (data: ProductSetType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeProduct: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        if (value.length) {
            delete newErrors.PRODUCTS;
        } else {
            newErrors.PRODUCTS = true;
        }
        if (errorKey) {
            newErrors[errorKey] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleDeleteProductSet = () => {
        setConfirmParams({
            title: translate('productSetDetail.delete'),
            content: translate('productSetDetail.deleteConfirm'),
            action: () => dispatch(deleteProductSetFetch(setSKU))
        });
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);
        if (!editData?.PRODUCTS?.length) {
            newFormErrors.PRODUCTS = true;
        }

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(newProductSet ? 'productSetDetail.create' : 'productSetDetail.save'),
                content: translate(newProductSet ? 'productSetDetail.createConfirm' : 'productSetDetail.saveConfirm'),
                action: () => {
                    const formatData = {
                        ...editData,
                        PRODUCTS: editData?.PRODUCTS?.map((product) => {
                            const formatProduct = { ...product };
                            if ((formatProduct.ID || -1) < 0) {
                                delete formatProduct.ID;
                            }
                            return formatProduct;
                        })
                    };
                    if (newProductSet) {
                        dispatch(createProductSetDetailFetch(formatData));
                    } else {
                        dispatch(updateProductSetDetailFetch({ setSKU, data: formatData }));
                    }
                }
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock
                newProductSet={newProductSet}
                disableEdit={disableEdit}
                permissionDelete={permissionDelete}
                handleSave={handleSave}
                handleDeleteProductSet={handleDeleteProductSet}
                handleNavigateToProductDetail={handleNavigateToProductDetail}
            />
            {loading && <Loading />}

            <FormBlock sx={{ marginBottom: sizes.blocks_top_MG }}>
                <form ref={formRef}>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newProductSet={newProductSet}
                        disableEdit={disableEdit}
                    />
                </form>
            </FormBlock>
            <FormBlock>
                <NewProductBlock
                    data={editData.PRODUCTS || []}
                    onChange={(value: any) => onChange('PRODUCTS', value, false)}
                    disableEdit={disableEdit}
                    editErrors={editErrors}
                />
            </FormBlock>
            <form ref={productsFormRef}>
                <ProductsTable
                    newProductSet={newProductSet}
                    data={editData.PRODUCTS}
                    editErrors={editErrors}
                    onChange={(value: any, error: boolean, errorKey?: string) => {
                        onChangeProduct('PRODUCTS', value, error, errorKey);
                    }}
                    disableEdit={disableEdit}
                />
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ProductSetDetailContainer;
